import React from 'react';
import './leaderboard.scss';
import SharedProfile from '../../../../shared-storage/profile';
import MeritApi from '../../../apis/merit-api';

const CATEGORIES = [
    { id: 'top_performer', label: 'Top Performer', icon: '🥇' },
    { id: 'self_learning', label: 'Self Learning Leader', icon: '🚀' },
    { id: 'consistency', label: 'Consistency Champion', icon: '🔥' },
    { id: 'rising_star', label: 'Rising Star', icon: '📈' }
];

const formatLastUpdated = (ts) => {
    if (!ts) return '';
    try {
        const date = new Date(ts);
        date.setDate(date.getDate() - 1);
        const options = { month: 'short', day: 'numeric', year: 'numeric' };
        return date.toLocaleDateString('en-US', options);
    } catch (e) {
        return '';
    }
};

const formatMonthNavigatorLabel = (date) => {
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
};

const TabBar = ({ activeCategory, onCategoryChange }) => {
    return (
        <div className="leaderboard-tab-bar">
            {CATEGORIES.map(cat => (
                <button
                    key={cat.id}
                    className={`tab-btn ${activeCategory === cat.id ? 'active' : ''}`}
                    onClick={() => onCategoryChange(cat.id)}
                >
                    <span className="tab-icon">{cat.icon}</span>
                    <span className="tab-label">{cat.label}</span>
                </button>
            ))}
        </div>
    );
};

const MonthNavigator = ({ currentMonthDate, onMonthChange, isCurrentMonth }) => {
    const label = formatMonthNavigatorLabel(currentMonthDate);
    return (
        <div className="month-navigator">
            <button className="nav-arrow prev" onClick={() => onMonthChange('prev')}>&larr;</button>
            <div className="month-label">{label}</div>
            <button
                className={`nav-arrow next ${isCurrentMonth ? 'disabled' : ''}`}
                onClick={() => onMonthChange('next')}
                disabled={isCurrentMonth}
            >
                &rarr;
            </button>
        </div>
    );
};

const LeaderboardHeader = ({ lastUpdated, currentMonthDate, onMonthChange, isCurrentMonth }) => {
    const timestampStr = formatLastUpdated(lastUpdated);
    return (
        <div className="leaderboard-header-bar">
            <div className="header-info">
                <h3 className="header-title">Class Rankings</h3>
                {timestampStr && <span className="header-timestamp">Last updated: {timestampStr}</span>}
            </div>
            <MonthNavigator
                currentMonthDate={currentMonthDate}
                onMonthChange={onMonthChange}
                isCurrentMonth={isCurrentMonth}
            />
        </div>
    );
};

const ColumnHeader = () => {
    return (
        <div className="column-header">
            <div className="col-rank">Rank</div>
            <div className="col-student">Student</div>
            <div className="col-merits">Merits</div>
        </div>
    );
};

const RankBadge = ({ variant, rank }) => {
    return (
        <div className={`rank-badge badge-${variant}`}>
            {rank || '-'}
        </div>
    );
};

const PodiumRow = ({ entry, variant, isCurrentUser, isCurrentMonth }) => {
    // Always highlight the current user regardless of which month is being viewed
    const applyUserHighlight = isCurrentUser;
    const rowClass = `lb-row lb-rank-${variant} ${applyUserHighlight ? 'is-user-podium' : ''}`;

    return (
        <div className={rowClass}>
            <div className="col-rank">
                <RankBadge variant={variant} rank={entry.rank} />
            </div>
            <div className="col-student">
                <span className="student-name">{entry.name}</span>
                {applyUserHighlight && <span className="student-sublabel">You</span>}
            </div>
            <div className="col-merits">{entry.merits}</div>
        </div>
    );
};

const YouRow = ({ entry }) => {
    return (
        <div className="lb-row lb-rank-you">
            <div className="col-rank">
                <RankBadge variant="you" rank={entry.rank} />
            </div>
            <div className="col-student">
                <span className="student-name">{entry.name}</span>
                <span className="student-sublabel">You · Rank {entry.rank || '-'}</span>
            </div>
            <div className="col-merits">{entry.merits}</div>
        </div>
    );
};

const OtherRow = ({ entry, index }) => {
    const alternatingClass = index % 2 === 0 ? 'lb-rank-plain-even' : 'lb-rank-plain-odd';
    return (
        <div className={`lb-row ${alternatingClass}`}>
            <div className="col-rank">
                <RankBadge variant="plain" rank={entry.rank} />
            </div>
            <div className="col-student">
                <span className="student-name">{entry.name}</span>
            </div>
            <div className="col-merits">{entry.merits}</div>
        </div>
    );
};

const SkeletonRow = () => {
    return (
        <div className="skeleton-row">
            <div className="skeleton-badge"></div>
            <div className="skeleton-text"></div>
            <div className="skeleton-merits"></div>
        </div>
    );
};

const getPodiumVariant = (rank, index) => {
    if (rank === 1) return 'gold';
    if (rank === 2) return 'silver';
    if (rank === 3) return 'bronze';
    const fallback = ['gold', 'silver', 'bronze'];
    return fallback[index] || 'gold';
};

const LeaderboardBody = ({
    podium,
    others,
    student,
    showYouRow,
    isLoading,
    isNavigating,
    isError,
    onRetry,
    isCurrentMonth
}) => {
    if (isLoading) {
        return (
            <div className="leaderboard-body-list">
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="state-container">
                <div className="state-icon">⚠️</div>
                <div className="state-title">Unable to load rankings</div>
                <div className="state-subtitle">There was an error loading the leaderboard data. Please try again.</div>
                <button className="btn-retry" onClick={onRetry}>Retry</button>
            </div>
        );
    }

    if (podium.length === 0 && others.length === 0 && !showYouRow) {
        return (
            <div className="state-container">
                <div className="state-icon">🏆</div>
                <div className="state-title">No data available for this month</div>
                <div className="state-subtitle">Complete activities to appear on the leaderboard!</div>
            </div>
        );
    }

    return (
        <div className={`leaderboard-body-list ${isNavigating ? 'is-navigating' : ''}`}>
            <div className="leaderboard-scroll-body">
                {podium.map((entry, idx) => {
                    const variant = getPodiumVariant(entry.rank, idx);
                    return (
                        <PodiumRow
                            key={`podium-${entry.userId || idx}`}
                            entry={entry}
                            variant={variant}
                            isCurrentUser={entry.isCurrentUser}
                            isCurrentMonth={isCurrentMonth}
                        />
                    );
                })}

                {showYouRow && student && <YouRow entry={student} />}

                {others.map((entry, idx) => (
                    <OtherRow
                        key={`other-${entry.userId || idx}`}
                        entry={entry}
                        index={idx}
                    />
                ))}
            </div>
        </div>
    );
};

export default class Leaderboard extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            activeCategory: 'top_performer',
            currentMonthDate: (() => {
                const today = new Date();
                return new Date(today.getFullYear(), today.getMonth(), 1);
            })(),
            leaderboardData: [],
            lastUpdated: null,
            isLoading: true,
            isNavigating: false,
            isError: false
        };
    }

    componentDidMount() {
        this.fetchClassStudents();
    }

    fetchClassStudents = (category, monthDate) => {
        let userInfo = SharedProfile.getProfile();
        let activeCategory = category || this.state.activeCategory;
        let activeMonthDate = monthDate || this.state.currentMonthDate;

        // If it is the first load (no data yet), set isLoading to true. Otherwise, set isNavigating to true.
        const isFirstLoad = this.state.leaderboardData.length === 0;
        if (isFirstLoad) {
            this.setState({ isLoading: true, isError: false });
        } else {
            this.setState({ isNavigating: true, isError: false });
        }

        if (userInfo) {
            const categoryMap = {
                'top_performer': 'Top Performer',
                'self_learning': 'Self Learning Leader',
                'consistency': 'Consistency Champion',
                'rising_star': 'Rising Star'
            };
            const apiCategory = categoryMap[activeCategory] || 'Top Performer';

            if (activeCategory === 'consistency' || activeCategory === 'rising_star') {
                this.setState({
                    leaderboardData: [],
                    lastUpdated: null,
                    isLoading: false,
                    isNavigating: false,
                    isError: false
                });
                return;
            }

            const year = activeMonthDate.getFullYear();
            const monthStr = String(activeMonthDate.getMonth() + 1).padStart(2, '0');
            const apiMonth = `${year}-${monthStr}`;

            let payload = {
                userId: userInfo.userId || userInfo._id,
                category: apiCategory,
                month: apiMonth,
                subjectId: userInfo.subjectId || null
            };

            MeritApi.getStudentMeritLeaderboard(payload).then(response => {
                if (response && response.status === 1) {
                    let formattedData = response.data.map(item => {
                        let merits = item.finalMerits ?? 0;
                        if (apiCategory === 'Self Learning Leader') {
                            merits = item.selfLearningMeritsAtRank ?? item.selfLearningMerits ?? merits;
                        } else if (apiCategory === 'Top Performer') {
                            merits = item.totalActivityMeritsAtRank ?? item.totalActivityMerits ?? merits;
                        }
                        
                        return {
                            rank: item.rank ?? null,
                            name: item.name || "Unknown Student",
                            merits: merits,
                            userId: item.userId,
                            isCurrentUser: item.isCurrentUser || false
                        };
                    });

                    formattedData.sort((a, b) => {
                        if (a.rank === null && b.rank === null) return 0;
                        if (a.rank === null) return 1;
                        if (b.rank === null) return -1;
                        return a.rank - b.rank;
                    });

                    this.setState({
                        leaderboardData: formattedData,
                        lastUpdated: response.lastUpdated || null,
                        isLoading: false,
                        isNavigating: false
                    });
                } else {
                    this.setState({
                        leaderboardData: [],
                        lastUpdated: null,
                        isLoading: false,
                        isNavigating: false
                    });
                }
            }).catch(err => {
                console.error("Error fetching leaderboard", err);
                this.setState({ isLoading: false, isNavigating: false, isError: true });
            });
        } else {
            this.setState({ isLoading: false, isNavigating: false, isError: true });
        }
    }

    handleMonthChange = (direction) => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();

        const currentSel = this.state.currentMonthDate;
        let newSel;

        if (direction === 'prev') {
            newSel = new Date(currentSel.getFullYear(), currentSel.getMonth() - 1, 1);
            this.setState({ currentMonthDate: newSel }, () => {
                this.fetchClassStudents();
            });
        } else if (direction === 'next') {
            newSel = new Date(currentSel.getFullYear(), currentSel.getMonth() + 1, 1);
            if (newSel <= new Date(currentYear, currentMonth, 1)) {
                this.setState({ currentMonthDate: newSel }, () => {
                    this.fetchClassStudents();
                });
            }
        }
    }

    handleCategoryChange = (category) => {
        this.setState({ activeCategory: category }, () => {
            this.fetchClassStudents();
        });
    }

    render() {
        const {
            leaderboardData,
            isLoading,
            isNavigating,
            isError,
            activeCategory,
            currentMonthDate,
            lastUpdated
        } = this.state;

        const today = new Date();
        const isCurrentMonth = currentMonthDate.getFullYear() === today.getFullYear() &&
            currentMonthDate.getMonth() === today.getMonth();

        // Show ALL students with rank 1, 2, or 3 — do NOT slice(0,3) because
        // ties (e.g. two rank-1 students) would push the actual rank-3 student off the podium
        const podium = leaderboardData.filter(item => item.rank !== null && item.rank <= 3);
        const student = leaderboardData.find(item => item.isCurrentUser) || null;

        // Show YouRow whenever the current user is outside the top 3 (regardless of current or past month)
        // This ensures they are never invisible when navigating past months
        const showYouRow = !!(student && (student.rank === null || student.rank > 3));

        // Always exclude the current user from the "others" list — they're shown via YouRow instead
        const others = leaderboardData.filter(item => (item.rank === null || item.rank > 3) && !item.isCurrentUser);

        return (
            <div className="leaderboard-container container mt-4">
                <div className="leaderboard-title-wrapper">
                    <h2 className="leaderboard-title">Leaderboard</h2>
                </div>

                <div className="leaderboard-card">
                    <TabBar
                        activeCategory={activeCategory}
                        onCategoryChange={this.handleCategoryChange}
                    />

                    <LeaderboardHeader
                        lastUpdated={lastUpdated}
                        currentMonthDate={currentMonthDate}
                        onMonthChange={this.handleMonthChange}
                        isCurrentMonth={isCurrentMonth}
                    />

                    <ColumnHeader />

                    <div className={`loading-bar ${isNavigating ? 'active' : ''}`} />

                    <LeaderboardBody
                        podium={podium}
                        others={others}
                        student={student}
                        showYouRow={showYouRow}
                        isLoading={isLoading}
                        isNavigating={isNavigating}
                        isError={isError}
                        onRetry={() => this.fetchClassStudents()}
                        isCurrentMonth={isCurrentMonth}
                    />
                </div>
            </div>
        );
    }
}
